import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 md:pt-28 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-6">
              <i className="ri-question-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-sm text-foreground-600 mb-8">The article you are looking for does not exist or has been moved.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <article>
          <header className="pt-24 md:pt-28 pb-8 md:pb-12 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to="/shopping-guide" className="hover:text-primary-600 transition-colors whitespace-nowrap">Shopping Guide</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-400 whitespace-nowrap">{article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}</span>
              </div>
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                {article.metaDescription}
              </p>
              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-accent-700 text-sm font-bold">VG</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">Vigorbuy Guide Team</p>
                  <p className="text-xs text-foreground-500">Updated July 2026 · {article.readTime}</p>
                </div>
              </div>
            </div>
          </header>

          <div className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="prose-content">
              {article.blocks.map((block, i) => {
                if (block.type === 'h2') {
                  return (
                    <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                      {block.content}
                    </h2>
                  );
                }
                if (block.type === 'h3') {
                  return (
                    <h3 key={i} className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-8 mb-3">
                      {block.content}
                    </h3>
                  );
                }
                if (block.type === 'p') {
                  return (
                    <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: block.content || '' }} />
                  );
                }
                if (block.type === 'image' && block.src) {
                  return (
                    <div key={i} className="my-8 rounded-xl overflow-hidden">
                      <img
                        src={block.src}
                        alt={block.alt || ''}
                        className="w-full h-auto rounded-xl"
                      />
                    </div>
                  );
                }
                if (block.type === 'takeaways' && block.items) {
                  return (
                    <div key={i} className="bg-background-100 rounded-xl p-6 md:p-8 my-8">
                      <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-3">
                        Key Takeaways
                      </h3>
                      <ul className="space-y-2">
                        {block.items.map((item, j) => (
                          <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-700">
                            <i className="ri-check-line text-accent-600 mt-0.5"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
                if (block.type === 'cta') {
                  return (
                    <div key={i} className="flex flex-col sm:flex-row items-center gap-4 mt-10 p-6 rounded-xl bg-primary-50 border border-primary-100">
                      <div className="flex-1">
                        <p className="font-heading font-semibold text-foreground-900 mb-1">Ready to start exploring?</p>
                        <p className="text-sm text-foreground-600">{block.content}</p>
                      </div>
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        Vigorbuy Spreadsheet
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.metaDescription,
              image: article.heroImage,
              author: {
                '@type': 'Organization',
                name: 'Vigorbuy Guide Team',
              },
              datePublished: article.datePublished,
              dateModified: article.datePublished,
              publisher: {
                '@type': 'Organization',
                name: 'Vigorbuy Spreadsheet Guide',
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}